import React, { useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'

// Material UI
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import Tooltip from '@material-ui/core/Tooltip'

// Form
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

// Constants
import { fieldsByService, InputDefault } from 'utils/Constants'

// Icons
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import Cancel from '@material-ui/icons/Cancel'

// Inputs
import { TextInput } from '../Inputs/TextInput'
import { SelectInput } from '../Inputs/SelectInput'
import { BooleanInput } from '../Inputs/BooleanInput'

// Components
import InfoModal from '../Components/InfoModal'

// Style
import { makeStyles } from '@material-ui/core/styles'
import styles from 'assets/jss/material-dashboard-pro-react/views/Apps/nitorInsightsStyle.js'

// Hooks
import {
  useUpdateTransactionConnection,
  useValidateTransactionsConnection
} from 'hooks/useInvoiceConf'

const useStyles = makeStyles(styles)

const schema = yup.object().shape({
  type: yup.string().optional(),
  realm: yup.string().when('type', (type, schema) => {
    switch (type) {
      case 'ariba':
      case 'aribaItk':
        return schema.min(1).required()
      default:
        return schema.optional()
    }
  }),
  apiKey: yup.string().when('type', (type, schema) => {
    switch (type) {
      case 'ariba':
        return schema.min(1).required()
      default:
        return schema.optional()
    }
  }),
  clientId: yup.string().when('type', (type, schema) => {
    switch (type) {
      case 'ariba':
      case 'coupa':
        return schema.min(1).required()
      default:
        return schema.optional()
    }
  }),
  secret: yup.string().when('type', (type, schema) => {
    switch (type) {
      case 'ariba':
        return schema.min(1).required()
      default:
        return schema.optional()
    }
  }),
  siteUrl: yup.string().when('type', (type, schema) => {
    switch (type) {
      case 'coupa':
        return schema.min(1).required()
      default:
        return schema.optional()
    }
  }),
  clientSecret: yup.string().when('type', (type, schema) => {
    switch (type) {
      case 'coupa':
        return schema.min(1).required()
      default:
        return schema.optional()
    }
  }),
  siteRegion: yup.string().when('type', (type, schema) => {
    switch (type) {
      case 'aribaItk':
        return schema.min(1).required()
      default:
        return schema.optional()
    }
  }),
  authenticationType: yup.string().when('type', (type, schema) => {
    switch (type) {
      case 'aribaItk':
        return schema.min(1).required()
      default:
        return schema.optional()
    }
  }),
  certificate: yup.string().when('authenticationType', (authenticationType, schema) => {
    switch (authenticationType) {
      case 'certificate':
        return schema.min(1).required()
      default:
        return schema.optional()
    }
  }),
  sharedSecret: yup.string().when('authenticationType', (authenticationType, schema) => {
    switch (authenticationType) {
      case 'sharedSecret':
        return schema.min(1).required()
      default:
        return schema.optional()
    }
  })
})

const connectionObj = {
  realm: '',
  apiKey: '',
  clientId: '',
  secret: '',
  siteUrl: '',
  clientSecret: '',
  siteRegion: '',
  authenticationType: '',
  certificate: '',
  sharedSecret: ''
}

export default function InvoiceGroupForm(props) {
  const classes = useStyles()
  const { group, app, setCreateError, setSubmitSuccess, setTab } = props
  const [connectionForm] = useState(connectionObj)
  const queryClient = useQueryClient()
  const [connectionConnected, setConnectionConnected] = useState(undefined)
  const [connectionMessage, setConnectionMessage] = useState('')
  const [showModal, setShowModal] = useState({ open: false, message: '' })

  const {
    handleSubmit,
    formState: { isDirty },
    control,
    getValues,
    reset
  } = useForm({
    mode: 'all',
    defaultValues: connectionForm,
    resolver: yupResolver(schema),
    shouldUnregister: false
  })

  useEffect(() => {
    if (isDirty) {
      setConnectionConnected(undefined)
      setConnectionMessage('')
    }
  }, [isDirty, setConnectionConnected, setConnectionMessage])

  useEffect(() => {
    if (!app.data?.data) {
      return
    }

    switch (app.data?.data.service) {
      case 'ariba':
        if (app.data?.data.params.ariba && app.data?.data.params.ariba[group]) {
          setConnectionConnected(true)
          reset(app.data?.data.params.ariba[group])
        }
        break
      case 'coupa':
        if (app.data?.data.params.coupa && app.data?.data.params.coupa.api) {
          setConnectionConnected(true)
          reset(app.data?.data.params.coupa.api)
        }
        break
      default:
        break
    }
  }, [app.data.data, connectionForm, group, reset])

  const {
    mutate: updateTransactionConnection,
    isLoading: isLoadingUpdateConnection
  } = useUpdateTransactionConnection({
    appId: app.data?.data.appId
  })

  const {
    mutate: validateTransactionConnection,
    isLoading: isLoadingValidateConnection
  } = useValidateTransactionsConnection({
    appId: app.data?.data.appId
  })

  function modifyData(data) {
    let newData = {}
    switch (app.data?.data.service) {
      case 'ariba':
        switch (group) {
          case 'sourcingApi':
          case 'buyerItkApi':
            newData = {
              group: group,
              service: app.data?.data.service,
              ariba: {
                region: app.data?.data.ariba ? app.data?.data.ariba.region : 'us',
                [group]: {
                  authenticationType: data.authenticationType,
                  sharedSecret: data.sharedSecret,
                  realm: data.realm,
                  siteRegion: data.siteRegion
                }
              }
            }
            break
          default:
            newData = {
              group: group,
              service: app.data?.data.service,
              ariba: {
                region: app.data?.data.ariba ? app.data?.data.ariba.region : 'us',
                [group]: {
                  realm: data.realm
                }
              }
            }
            if (data.apiKey !== InputDefault) {
              newData.ariba[group].apiKey = data.apiKey
            }
            if (data.clientId !== InputDefault) {
              newData.ariba[group].clientId = data.clientId
            }
            if (data.secret !== InputDefault) {
              newData.ariba[group].secret = data.secret
            }
            break
        }
        break
      case 'coupa':
        newData = {
          service: app.data?.data.service,
          coupa: {
            api: {
              siteUrl: data.siteUrl
            }
          }
        }
        if (data.clientId !== InputDefault) {
          newData.coupa.api.clientId = data.clientId
        }
        if (data.clientSecret !== InputDefault) {
          newData.coupa.api.clientSecret = data.clientSecret
        }
        break
      default:
        break
    }
    return newData
  }

  function onSubmitHook(data) {
    data = modifyData(data)
    updateTransactionConnection(
      { data },
      {
        onSettled: () => {
          queryClient.invalidateQueries(['Apps', app.data?.data.appId])
        },
        onSuccess: response => {
          if (response.status === 200) {
            setSubmitSuccess({ message: 'Saved', isOpen: true })
            app.refetch({ throwOnError: true })
            setTab(0)
          }
        },
        onError: error => {
          setCreateError({
            message: error.response?.data?.message ?? 'Something went wrong, try again later',
            isOpen: true,
            color: 'danger'
          })
        }
      }
    )
  }

  function typeForm() {
    let fields = []
    if (!group || group === '') {
      return
    }
    //get service
    fields = fieldsByService[(app.data?.data.service)]

    return (
      <Grid container justifyContent="center" spacing={3}>
        {fields.map(field => inputs(field))}
      </Grid>
    )
  }

  function inputs(field) {
    let jsxElements = []
    switch (field.type) {
      case 'boolean':
        jsxElements.push(
          <BooleanInput key={field.key} name={field.key} control={control} label={field.label} />
        )
        break
      case 'select':
        jsxElements.push(
          <SelectInput
            key={field.key}
            name={field.key}
            control={control}
            label={field.label}
            values={field.values}
          />
        )
        break
      case 'text':
      case 'password':
      case 'number':
        jsxElements.push(
          <TextInput
            key={field.key}
            name={field.key}
            control={control}
            type={field.type}
            label={field.label}
            multiline={false}
          />
        )
        break
      case 'conditional':
        const conditional = getValues(field.inputCondition)
        if (field.conditional && conditional === field.valueCondition) {
          jsxElements.push(
            <TextInput
              key={field.key}
              name={field.key}
              control={control}
              label={field.label}
              type={field.typeInput}
              multiline={field.multiline}
              rows={field.rows}
            />
          )
        }
        break
      default:
        break
    }

    return jsxElements.map(jsxElement => jsxElement)
  }

  function checkConnection() {
    const data = modifyData(getValues())
    validateTransactionConnection(
      { data },
      {
        onSettled: () => {
          queryClient.invalidateQueries(['Apps', app.data?.data.appId])
        },
        onSuccess: response => {
          if (response.data.valid) {
            setConnectionConnected(true)
            setConnectionMessage('')
          } else {
            let defaultError = {
              error: 'invalid_request',
              error_description: 'Connection Error'
            }
            defaultError = JSON.stringify(defaultError, null, '\t')
            setConnectionConnected(false)
            setConnectionMessage(JSON.stringify(response.data.data) || defaultError)
          }
        },
        onError: error => {
          setConnectionConnected(false)
          setCreateError({
            message: error.response?.data?.message ?? 'Something went wrong, try again later',
            isOpen: true,
            color: 'danger'
          })
        }
      }
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmitHook)}>
      {typeForm()}
      <Grid container justifyContent="center" spacing={3}>
        <Grid item xs={12} sm={12} md={12} style={{ textAlign: 'right' }}>
          <Tooltip title="Check connection">
            <Button
              color={
                connectionConnected !== undefined
                  ? connectionConnected
                    ? 'primary'
                    : 'secondary'
                  : 'default'
              }
              size="small"
              onClick={() => checkConnection()}
              variant="contained"
              disabled={isLoadingValidateConnection}
            >
              {connectionConnected !== undefined ? (
                connectionConnected ? (
                  <>
                    <CheckBoxIcon /> Connected
                  </>
                ) : (
                  <>
                    <Cancel /> Connection Error
                  </>
                )
              ) : isLoadingValidateConnection ? (
                'Loading...'
              ) : (
                'Test Connectivityy'
              )}
            </Button>
          </Tooltip>
        </Grid>
        {connectionMessage ? (
          <Grid item xs={12} sm={12} md={12} lg={12} style={{ textAlign: 'right' }}>
            <Tooltip title="View connection error details">
              <Button
                className={classes.documentation}
                style={{ color: '#1769ff' }}
                onClick={() => setShowModal({ open: true, message: connectionMessage })}
              >
                Error details
              </Button>
            </Tooltip>
          </Grid>
        ) : null}
        <Grid item xs={12} style={{ textAlign: 'center' }}>
          <Button
            className={classes.bgColorPrimary}
            type="submit"
            variant="contained"
            color="primary"
            disabled={
              isLoadingUpdateConnection || !connectionConnected || isLoadingValidateConnection
            }
          >
            Save
          </Button>
        </Grid>
        <InfoModal
          showModal={showModal.open}
          onClose={() => setShowModal({ open: false, message: '' })}
          warningMessage={showModal.message}
          title="Error connection details"
        />
      </Grid>
    </form>
  )
}
